import bcrypt from "bcryptjs";
import {
	getRedirectResult,
	onAuthStateChanged,
	signInWithPopup,
	signInWithRedirect,
	signOut,
} from "firebase/auth";
import {
	doc,
	getDoc,
	getFirestore,
	setDoc,
} from "firebase/firestore";
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { auth, googleProvider } from "../firebase";
import { shouldUseRedirect } from "../utils/browserDetection";

const UserContext = createContext();
export const useUser = () => useContext(UserContext);

// Cache duration: 24 hours in milliseconds
const CACHE_DURATION = 24 * 60 * 60 * 1000;

// Function URLs for Firebase Functions v2
const FUNCTION_URLS = {
	// Local development URLs
	local: {
		getSubscriptionStatus:
			"http://localhost:5001/dreamjob-ca923/us-central1/getSubscriptionStatus",
		createCheckoutSession:
			"http://localhost:5001/dreamjob-ca923/us-central1/createCheckoutSession",
		createPortalSession:
			"http://localhost:5001/dreamjob-ca923/us-central1/createPortalSession",
		createDonationSession:
			"http://localhost:5001/dreamjob-ca923/us-central1/createDonationSession",
		createSubscriptionWithDonationSession:
			"http://localhost:5001/dreamjob-ca923/us-central1/createSubscriptionWithDonationSession",
	},
	// Production URLs - need to be updated with correct project ID
	production: {
		getSubscriptionStatus:
			"https://getsubscriptionstatus-rflhc7dvwq-uc.a.run.app",
		createCheckoutSession:
			"https://createcheckoutsession-rflhc7dvwq-uc.a.run.app",
		createPortalSession: "https://createportalsession-rflhc7dvwq-uc.a.run.app",
		createDonationSession:
			"https://createdonationsession-rflhc7dvwq-uc.a.run.app",
		createSubscriptionWithDonationSession:
			"https://createsubscriptionwithdonationsession-rflhc7dvwq-uc.a.run.app",
	},
};

// Helper function to call Firebase Functions v2 HTTPS endpoints
const callFunction = async (functionName, data = {}) => {
	try {
		// Determine if we're in local development
		const isLocalDev =
			process.env.NODE_ENV === "development" &&
			(window.location.hostname === "localhost" ||
				window.location.hostname.match(/^\d+\.\d+\.\d+\.\d+$/));

		const urlSet = isLocalDev ? FUNCTION_URLS.local : FUNCTION_URLS.production;
		const url = urlSet[functionName];

		if (!url) {
			throw new Error(`Function ${functionName} not found`);
		}

		console.log(`[callFunction] Calling ${functionName} with data:`, data);
		console.log(`[callFunction] URL: ${url}`);

		// Add user agent info for debugging mobile issues
		const userAgent = navigator.userAgent;
		const isIOS = /iPad|iPhone|iPod/.test(userAgent);
		const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);

		console.log(
			`[callFunction] Device info - iOS: ${isIOS}, Safari: ${isSafari}`,
		);

		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(data),
			// Add explicit credentials for iPad compatibility
			credentials: "same-origin",
			// Ensure proper cache handling on mobile
			cache: "no-cache",
		});

		console.log(`[callFunction] Response status: ${response.status}`);

		if (!response.ok) {
			const errorText = await response.text();
			console.error(`[callFunction] Error response:`, errorText);

			// More detailed error for mobile debugging
			let errorMessage = `HTTP error! status: ${response.status}`;
			try {
				const errorJson = JSON.parse(errorText);
				errorMessage += `, message: ${JSON.stringify(errorJson)}`;
			} catch {
				errorMessage += `, response: ${errorText}`;
			}

			throw new Error(errorMessage);
		}

		const result = await response.json();
		console.log(`[callFunction] Success response:`, result);
		return result;
	} catch (error) {
		console.error(`[callFunction] Network or parsing error:`, error);
		throw error;
	}
};

// Function to get cached subscription status
const getCachedSubscriptionStatus = (userId) => {
	try {
		const cacheKey = `subscriptionStatus_${userId}`;
		const cached = localStorage.getItem(cacheKey);

		if (!cached) {
			console.log("[Subscription Cache] No cached data found");
			return null;
		}

		const { data, timestamp } = JSON.parse(cached);
		const now = Date.now();

		if (now - timestamp > CACHE_DURATION) {
			console.log("[Subscription Cache] Cache expired, removing");
			localStorage.removeItem(cacheKey);
			return null;
		}

		console.log("[Subscription Cache] Using cached data:", data);
		return data;
	} catch (error) {
		console.error("[Subscription Cache] Error reading cache:", error);
		return null;
	}
};

// Function to set cached subscription status
const setCachedSubscriptionStatus = (userId, data) => {
	try {
		const cacheKey = `subscriptionStatus_${userId}`;
		const cacheData = {
			data,
			timestamp: Date.now(),
		};

		localStorage.setItem(cacheKey, JSON.stringify(cacheData));
		console.log("[Subscription Cache] Data cached successfully");
	} catch (error) {
		console.error("[Subscription Cache] Error setting cache:", error);
	}
};

// Function to clear cached subscription status
const clearCachedSubscriptionStatus = (userId) => {
	try {
		const cacheKey = `subscriptionStatus_${userId}`;
		localStorage.removeItem(cacheKey);
		console.log("[Subscription Cache] Cache cleared for user");
	} catch (error) {
		console.error("[Subscription Cache] Error clearing cache:", error);
	}
};

export const UserProvider = ({ children }) => {
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);
	const [subscriptionStatus, setSubscriptionStatus] = useState(null);
	const [lastSubscriptionCheck, setLastSubscriptionCheck] = useState(null);
	const subscriptionStatusRef = useRef(subscriptionStatus);
	// Prevent redundant auth updates when Firebase fires multiple times for the same user (avoids loop)
	const lastAuthUidRef = useRef(null);
	const [userData, setUserData] = useState(() => {
		const savedData = localStorage.getItem("userData");
		return savedData
			? JSON.parse(savedData)
			: {
					name: "",
					favoriteColor: "#51cf66",
					avatar: "",
					bio: "",
					createdAt: new Date().toISOString(),
				};
	});

	// Keep ref in sync with state
	useEffect(() => {
		subscriptionStatusRef.current = subscriptionStatus;
	}, [subscriptionStatus]);

	// Function to check subscription status with caching
	const checkSubscriptionStatus = useCallback(
		async (userId, forceRefresh = false) => {
			try {
				// Check if we're in local development mode
				const isLocalDev =
					process.env.NODE_ENV === "development" &&
					(window.location.hostname === "localhost" ||
						window.location.hostname.match(/^\d+\.\d+\.\d+\.\d+$/));

				// For local development, try to call the local functions first, but have a fallback
				if (isLocalDev) {
					console.log(
						"[Subscription] Local development detected - trying local functions first",
					);
					try {
						// Try to call local function first
						const actualUserId = userId || user?.uid;
						if (!actualUserId) {
							console.log("[Subscription] No user ID available");
							return null;
						}

						const result = await callFunction("getSubscriptionStatus", {
							uid: actualUserId,
						});

						if (result) {
							console.log(
								"[Subscription] Local function call successful:",
								result,
							);
							setSubscriptionStatus(result);
							return result;
						}
					} catch (localError) {
						console.warn(
							"[Subscription] Local function call failed, using mock data:",
							localError.message,
						);
						// Fallback to mock subscription for local development
						const mockSubscription = {
							status: "active",
							plan: "pro",
							expiresAt: Date.now() + 30 * 24 * 60 * 60 * 1000, // 30 days from now
						};
						setSubscriptionStatus(mockSubscription);
						return mockSubscription;
					}
				}

				const actualUserId = userId || user?.uid;

				if (!actualUserId) {
					console.log("[Subscription] No user ID available");
					return null;
				}

				// Check if we should use cached data
				if (!forceRefresh) {
					const cached = getCachedSubscriptionStatus(actualUserId);
					if (cached) {
						console.log("[Subscription] Using cached subscription status");
						setSubscriptionStatus(cached);
						return cached;
					}
				}

				// Prevent rapid successive calls
				const now = Date.now();
				if (lastSubscriptionCheck && now - lastSubscriptionCheck < 5000) {
					console.log("[Subscription] Throttling API call (too recent)");
					return subscriptionStatusRef.current;
				}

				setLastSubscriptionCheck(now);

				console.log(
					"[Subscription] Fetching fresh subscription status for user:",
					user?.email,
				);

				console.log("[Subscription] Calling getSubscriptionStatus function...");
				const result = await callFunction("getSubscriptionStatus", {
					uid: actualUserId,
				});
				console.log("[Subscription] Raw result from function:", result);

				if (result) {
					console.log("[Subscription] Setting subscription status:", result);

					// Cache the result
					setCachedSubscriptionStatus(actualUserId, result);

					setSubscriptionStatus(result);
					return result;
				} else {
					console.warn("[Subscription] No data in result:", result);
					return null;
				}
			} catch (error) {
				console.error("[Subscription] Error checking status:", error);

				// If API fails, try to use cached data even if expired
				const actualUserId = userId || user?.uid;
				if (actualUserId) {
					const cacheKey = `subscriptionStatus_${actualUserId}`;
					const cached = localStorage.getItem(cacheKey);
					if (cached) {
						try {
							const { data } = JSON.parse(cached);
							console.log(
								"[Subscription] Using expired cache due to API error",
							);
							setSubscriptionStatus(data);
							return data;
						} catch (parseError) {
							console.error(
								"[Subscription] Error parsing cached data:",
								parseError,
							);
						}
					}
				}

				return null;
			}
		},
		[user, lastSubscriptionCheck], // Removed subscriptionStatus to prevent recreation loop
	);

	// Function to force refresh subscription status (for after payments)
	const refreshSubscriptionStatus = async (forceRefresh = true) => {
		try {
			if (!user?.uid) {
				console.log(
					"[refreshSubscriptionStatus] No user.uid available for refresh",
				);
				return { status: null };
			}

			// Always clear cache when force refreshing
			if (forceRefresh) {
				clearCachedSubscriptionStatus(user.uid);
				console.log(
					"[refreshSubscriptionStatus] Cache cleared for force refresh",
				);
			}

			console.log(
				`[refreshSubscriptionStatus] Checking status for user: ${user.email}`,
			);

			const result = await checkSubscriptionStatus(user.uid, forceRefresh);
			console.log(`[refreshSubscriptionStatus] Result:`, result);

			return result;
		} catch (error) {
			console.error("[refreshSubscriptionStatus] Error:", error);
			return { status: null, error: error.message };
		}
	};

	// Function to create donation session
	const createDonationSession = async (amount = 5, frequency = "one-time") => {
		try {
			console.log(
				"[UserContext] Creating donation session for user:",
				user.email,
				"amount:",
				amount,
				"frequency:",
				frequency,
			);

			const result = await callFunction("createDonationSession", {
				uid: user.uid,
				email: user.email,
				amount: amount,
				frequency: frequency,
			});

			console.log("[UserContext] Donation session result:", result);
			return result;
		} catch (error) {
			console.error("[UserContext] Error creating donation session:", error);
			throw error;
		}
	};

	// Function to create subscription with optional donation
	const createSubscriptionWithDonation = async (
		includeDonation = false,
		donationAmount = 0,
		donationFrequency = "one-time",
	) => {
		try {
			console.log(
				"[UserContext] Creating subscription session for user:",
				user.email,
				"includeDonation:",
				includeDonation,
				"donationAmount:",
				donationAmount,
				"donationFrequency:",
				donationFrequency,
			);

			const result = await callFunction(
				"createSubscriptionWithDonationSession",
				{
					uid: user.uid,
					email: user.email,
					includeDonation: includeDonation,
					donationAmount: donationAmount,
					donationFrequency: donationFrequency,
				},
			);

			console.log("[UserContext] Subscription session result:", result);
			return result;
		} catch (error) {
			console.error(
				"[UserContext] Error creating subscription session:",
				error,
			);
			throw error;
		}
	};

	// Function to save PIN to Firestore (hashed)
	const savePIN = async (pin) => {
		if (!user?.uid) {
			throw new Error("User not authenticated");
		}

		try {
			console.log("[PIN] Saving PIN for user:", user.email);

			// Hash the PIN with salt
			const saltRounds = 12;
			const hashedPIN = await bcrypt.hash(pin, saltRounds);

			const db = getFirestore();
			const userDocRef = doc(db, "users", user.uid);

			// Update or create user document with hashed PIN
			await setDoc(
				userDocRef,
				{
					pinHash: hashedPIN,
					pinCreatedAt: new Date().toISOString(),
					email: user.email,
				},
				{ merge: true },
			);

			console.log("[PIN] PIN saved successfully");
			return { success: true };
		} catch (error) {
			console.error("[PIN] Error saving PIN:", error);
			throw new Error("Failed to save PIN");
		}
	};

	// Function to verify PIN against stored hash
	const verifyPIN = async (pin) => {
		if (!user?.uid) {
			throw new Error("User not authenticated");
		}

		try {
			console.log("[PIN] Verifying PIN for user:", user.email);

			const db = getFirestore();
			const userDocRef = doc(db, "users", user.uid);
			const userDoc = await getDoc(userDocRef);

			if (!userDoc.exists()) {
				console.log("[PIN] No user document found");
				return {
					success: false,
					error: "No PIN found. Please contact support.",
				};
			}

			const userData = userDoc.data();
			const storedPinHash = userData.pinHash;

			if (!storedPinHash) {
				console.log("[PIN] No PIN hash found");
				return {
					success: false,
					error: "No PIN found. Please contact support.",
				};
			}

			// Compare provided PIN with stored hash
			const isValid = await bcrypt.compare(pin, storedPinHash);

			if (isValid) {
				console.log("[PIN] PIN verification successful");
				return { success: true };
			} else {
				console.log("[PIN] PIN verification failed");
				return { success: false, error: "Invalid PIN" };
			}
		} catch (error) {
			console.error("[PIN] Error verifying PIN:", error);
			return { success: false, error: "Failed to verify PIN" };
		}
	};

	// Function to check if user has a PIN set up
	const checkUserHasPIN = async () => {
		if (!user?.uid) {
			return false;
		}

		try {
			console.log("[PIN] Checking if user has PIN:", user.email);

			const db = getFirestore();
			const userDocRef = doc(db, "users", user.uid);
			const userDoc = await getDoc(userDocRef);

			if (!userDoc.exists()) {
				console.log("[PIN] No user document found");
				return false;
			}

			const userData = userDoc.data();
			const hasPIN = !!userData.pinHash;

			console.log("[PIN] User has PIN:", hasPIN);
			return hasPIN;
		} catch (error) {
			console.error("[PIN] Error checking PIN:", error);
			return false;
		}
	};

	// Check subscription status whenever user changes (with caching)
	useEffect(() => {
		if (user?.uid && !loading) {
			// Check if we already have a subscription status to avoid unnecessary calls
			const cached = getCachedSubscriptionStatus(user.uid);
			if (cached && subscriptionStatusRef.current) {
				console.log("[Subscription] Already have subscription status, skipping check");
				return;
			}

			console.log(
				"[Subscription] User loaded, checking subscription for:",
				user.email,
			);

			// First try to load from cache immediately to prevent UI jitter
			if (cached) {
				console.log("[Subscription] Loading cached data immediately");
				setSubscriptionStatus(cached);
			}

			// Then check if we need to refresh (this won't cause jitter since cache is loaded)
			checkSubscriptionStatus(user.uid);
		} else {
			console.log(
				"[Subscription] Clearing subscription status - user:",
				user?.email || "null",
				"loading:",
				loading,
			);
			setSubscriptionStatus(null);

			// Clear cache when user logs out
			if (!user?.uid && user !== undefined) {
				// Clear all subscription caches since we don't know the user ID
				Object.keys(localStorage).forEach((key) => {
					if (key.startsWith("subscriptionStatus_")) {
						localStorage.removeItem(key);
					}
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user, loading]); // checkSubscriptionStatus is stable via useCallback

	// Monitor auth state and check subscription
	useEffect(() => {
		console.log("[Auth] Setting up auth");

		const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
			const newUid = currentUser?.uid ?? null;
			// Skip redundant updates when Firebase fires again for the same user (stops re-render loop)
			if (lastAuthUidRef.current === newUid) {
				setLoading(false);
				return;
			}
			lastAuthUidRef.current = newUid;
			console.log("[Auth] State changed:", currentUser?.email || "null");
			setUser(currentUser);

			if (currentUser) {
				// Set up periodic check every 30 seconds to catch external subscription changes
				const intervalId = setInterval(async () => {
					console.log("[UserContext] Periodic subscription check");
					await checkSubscriptionStatus(currentUser.uid, false); // Use cache if available
				}, 30000);

				// Store interval ID to clear later
				window.subscriptionCheckInterval = intervalId;
			} else {
				// Clear periodic check when user logs out
				if (window.subscriptionCheckInterval) {
					clearInterval(window.subscriptionCheckInterval);
					window.subscriptionCheckInterval = null;
				}
			}

			setLoading(false);
		});

		// Check for redirect result on initial load
		getRedirectResult(auth)
			.then((result) => {
				if (result) {
					console.log("[Auth] Redirect successful:", result.user?.email);
				}
			})
			.catch((error) => {
				console.error("[Auth] Redirect error:", error);
			});

		return () => {
			console.log("[Auth] Cleaning up auth listener");
			unsubscribe();
			// Clean up interval on unmount
			if (window.subscriptionCheckInterval) {
				clearInterval(window.subscriptionCheckInterval);
				window.subscriptionCheckInterval = null;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Only run once on mount - checkSubscriptionStatus is stable enough via useCallback

	useEffect(() => {
		if (userData) {
			localStorage.setItem("userData", JSON.stringify(userData));
		}
	}, [userData]);

	const signInWithGoogle = async () => {
		try {
			if (shouldUseRedirect()) {
				console.log("[Auth] Starting redirect flow for Chrome on iOS");
				await signInWithRedirect(auth, googleProvider);
				return { success: true };
			} else {
				console.log("[Auth] Starting popup flow");
				const result = await signInWithPopup(auth, googleProvider);
				console.log("[Auth] Popup successful:", result.user?.email);
				return { success: true };
			}
		} catch (error) {
			console.error("[Auth] Sign in error:", error);
			return { success: false, error: error.message };
		}
	};

	const logOut = async () => {
		try {
			await signOut(auth);
			console.log("[Auth] Signed out successfully");
			return { success: true };
		} catch (error) {
			console.error("[Auth] Sign out error:", error);
			return { success: false, error: error.message };
		}
	};

	const updateUserData = (newData) => {
		setUserData((prev) => ({ ...prev, ...newData }));
	};

	const manageSubscription = async () => {
		try {
			console.log(
				"[UserContext] Starting manageSubscription for user:",
				user.email,
			);
			console.log("[UserContext] User UID:", user.uid);

			const result = await callFunction("createPortalSession", {
				uid: user.uid,
				email: user.email,
			});

			console.log("[UserContext] Portal session result:", result);
			return result;
		} catch (error) {
			console.error("[UserContext] Error creating portal session:", error);
			console.error("[UserContext] Error details:", {
				message: error.message,
				code: error.code,
				details: error.details,
				user: user?.email,
				uid: user?.uid,
			});
			throw error;
		}
	};

	const value = {
		user,
		userData,
		loading,
		subscriptionStatus,
		signInWithGoogle,
		logOut,
		updateUserData,
		manageSubscription,
		checkSubscriptionStatus,
		refreshSubscriptionStatus,
		createDonationSession,
		createSubscriptionWithDonation,
		savePIN,
		verifyPIN,
		checkUserHasPIN,
		// Debug function for manual cache clearing
		debugClearCache: () => {
			if (user?.uid) {
				clearCachedSubscriptionStatus(user.uid);
				console.log("[Debug] Cache cleared for user:", user.email);
				return refreshSubscriptionStatus(true);
			}
			console.log("[Debug] No user to clear cache for");
		},
	};

	return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
